import { buildFocusWorldPointSignalPatch } from "./map-selection-actions.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";

export const FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT =
  "fishymap:focus-world-point-request";
export const FISHYMAP_BOOKMARKS_CHANGE_EVENT = "fishymap:bookmarks-change";
export const FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT =
  "fishymap:zone-loot-profile-change";
export const FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT =
  "fishymap:loot-condition-option-select";
export const FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT =
  "fishymap:zone-info-tab-change";
export const FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT =
  "fishymap:character-activity-target-change";

const CONTRACT_VERSION = 1;
const BOOKMARK_DETAIL_MAX_BYTES = 1_048_576;
const BOOKMARK_ENTRY_LIMIT = 4_096;
const BOOKMARK_LAYER_SAMPLE_LIMIT = 4_096;
const JSON_MAX_DEPTH = 24;
const JSON_NODE_LIMIT = 8_192;
const JSON_COLLECTION_ENTRY_LIMIT = 4_096;
const JSON_KEY_MAX_LENGTH = 256;
const JSON_STRING_MAX_LENGTH = 65_536;
const IDENTIFIER_MAX_LENGTH = 128;
const SHORT_TEXT_MAX_LENGTH = 256;
const LABEL_MAX_LENGTH = 512;
const SELECTION_KEY_MAX_LENGTH = 512;
const ELEMENT_KIND_MAX_LENGTH = 64;
const POINT_KINDS = new Set(["", "clicked", "waypoint", "bookmark", "character"]);
const HISTORY_BEHAVIORS = new Set(["append", "navigate"]);
const LOOT_PROFILES = new Set(["rod", "harpoon", "unassigned"]);
const ZONE_INFO_PANES = new Set([
  "character",
  "samples",
  "hotspot",
  "landmark",
  "zone",
  "lineage",
  "territory",
  "trade",
]);
const BOOKMARK_FIELDS = new Set([
  "id",
  "worldX",
  "worldY",
  "worldZ",
  "label",
  "pointLabel",
  "layerSamples",
  "zoneRgb",
  "createdAt",
]);

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function record(value, path) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${path} must be an object`);
  }
  return value;
}

function assertOnlyKeys(value, allowedKeys, path) {
  const allowed = allowedKeys instanceof Set ? allowedKeys : new Set(allowedKeys);
  const unsupported = Object.keys(value).find((key) => !allowed.has(key));
  if (unsupported) {
    throw new TypeError(`${path} contains unsupported field ${unsupported}`);
  }
}

function assertExactKeys(value, expectedKeys, path) {
  assertOnlyKeys(value, expectedKeys, path);
  const missing = expectedKeys.find((key) => !Object.hasOwn(value, key));
  if (missing) {
    throw new TypeError(`${path} is missing required field ${missing}`);
  }
}

function versionedRecord(value, fields, path) {
  const source = record(value, path);
  assertExactKeys(source, ["version", ...fields], path);
  if (source.version !== CONTRACT_VERSION) {
    throw new TypeError(`${path}.version must be ${CONTRACT_VERSION}`);
  }
  return source;
}

function codePointLength(value) {
  return value.length;
}

function text(value, path, { allowEmpty = false, maxLength = SHORT_TEXT_MAX_LENGTH } = {}) {
  if (
    typeof value !== "string" ||
    value.trim() !== value ||
    (!allowEmpty && !value) ||
    codePointLength(value) > maxLength
  ) {
    const emptyDescription = allowEmpty ? "possibly empty" : "non-empty";
    throw new TypeError(
      `${path} must be a trimmed, ${emptyDescription} string of at most ${maxLength} characters`,
    );
  }
  return value;
}

function finiteNumber(value, path) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${path} must be a finite number`);
  }
  return value;
}

function nonNegativeSafeInteger(value, path) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${path} must be a non-negative safe integer`);
  }
  return value;
}

function safeInteger(value, path) {
  if (!Number.isSafeInteger(value)) {
    throw new TypeError(`${path} must be a safe integer`);
  }
  return value;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  Object.freeze(value);
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return value;
}

function frozenJson(value) {
  return deepFreeze(cloneJson(value));
}

function jsonValue(value, path, depth = 0, budget = { nodes: 0 }) {
  budget.nodes += 1;
  if (budget.nodes > JSON_NODE_LIMIT) {
    throw new TypeError(`${path} exceeds the JSON node limit`);
  }
  if (depth > JSON_MAX_DEPTH) {
    throw new TypeError(`${path} exceeds the JSON nesting limit`);
  }
  if (typeof value === "string") {
    if (codePointLength(value) > JSON_STRING_MAX_LENGTH) {
      throw new TypeError(`${path} exceeds the JSON string length limit`);
    }
    return value;
  }
  if (value === null || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return finiteNumber(value, path);
  }
  if (Array.isArray(value)) {
    if (value.length > JSON_COLLECTION_ENTRY_LIMIT) {
      throw new TypeError(`${path} exceeds the JSON collection entry limit`);
    }
    return value.map((entry, index) =>
      jsonValue(entry, `${path}[${index}]`, depth + 1, budget));
  }
  const source = record(value, path);
  const entries = Object.entries(source);
  if (entries.length > JSON_COLLECTION_ENTRY_LIMIT) {
    throw new TypeError(`${path} exceeds the JSON collection entry limit`);
  }
  const normalized = {};
  for (const [key, entry] of entries) {
    if (codePointLength(key) > JSON_KEY_MAX_LENGTH) {
      throw new TypeError(`${path} contains a JSON key that exceeds the length limit`);
    }
    normalized[key] = jsonValue(entry, `${path}.${key}`, depth + 1, budget);
  }
  return normalized;
}

function optionalText(value, path, maxLength) {
  return value == null
    ? null
    : text(value, path, { maxLength });
}

function normalizeStoredBookmark(value, path, jsonBudget) {
  const source = record(value, path);
  assertOnlyKeys(source, BOOKMARK_FIELDS, path);
  for (const required of ["id", "worldX", "worldY", "worldZ"]) {
    if (!Object.hasOwn(source, required)) {
      throw new TypeError(`${path} is missing required field ${required}`);
    }
  }
  const normalized = {
    id: text(source.id, `${path}.id`, { maxLength: IDENTIFIER_MAX_LENGTH }),
    worldX: safeInteger(source.worldX, `${path}.worldX`),
    worldY: finiteNumber(source.worldY, `${path}.worldY`),
    worldZ: safeInteger(source.worldZ, `${path}.worldZ`),
  };
  const label = optionalText(source.label, `${path}.label`, LABEL_MAX_LENGTH);
  const pointLabel = optionalText(
    source.pointLabel,
    `${path}.pointLabel`,
    LABEL_MAX_LENGTH,
  );
  const createdAt = optionalText(
    source.createdAt,
    `${path}.createdAt`,
    SHORT_TEXT_MAX_LENGTH,
  );
  if (label != null) {
    normalized.label = label;
  }
  if (pointLabel != null) {
    normalized.pointLabel = pointLabel;
  }
  if (Object.hasOwn(source, "layerSamples")) {
    if (
      !Array.isArray(source.layerSamples) ||
      source.layerSamples.length > BOOKMARK_LAYER_SAMPLE_LIMIT
    ) {
      throw new TypeError(
        `${path}.layerSamples must be an array of at most ${BOOKMARK_LAYER_SAMPLE_LIMIT} entries`,
      );
    }
    normalized.layerSamples = source.layerSamples.map((sample, index) => {
      const samplePath = `${path}.layerSamples[${index}]`;
      record(sample, samplePath);
      return jsonValue(sample, samplePath, 0, jsonBudget);
    });
  }
  if (Object.hasOwn(source, "zoneRgb")) {
    const zoneRgb = nonNegativeSafeInteger(source.zoneRgb, `${path}.zoneRgb`);
    if (zoneRgb > 0xffff_ffff) {
      throw new TypeError(`${path}.zoneRgb must fit in an unsigned 32-bit integer`);
    }
    normalized.zoneRgb = zoneRgb;
  }
  if (createdAt != null) {
    normalized.createdAt = createdAt;
  }
  return normalized;
}

function assertBookmarkDetailSize(detail) {
  const byteLength = new TextEncoder().encode(JSON.stringify(detail)).byteLength;
  if (byteLength > BOOKMARK_DETAIL_MAX_BYTES) {
    throw new RangeError(
      `FishyMap bookmark event detail exceeds ${BOOKMARK_DETAIL_MAX_BYTES} UTF-8 bytes`,
    );
  }
}

export function normalizeFocusWorldPointRequestDetail(value) {
  const source = versionedRecord(value, ["target"], "focus world point request");
  const target = record(source.target, "focus world point request.target");
  assertExactKeys(
    target,
    [
      "elementKind",
      "characterIdentityKey",
      "worldX",
      "worldZ",
      "pointKind",
      "pointLabel",
      "historyBehavior",
    ],
    "focus world point request.target",
  );
  const elementKind = text(target.elementKind, "focus world point request.target.elementKind", {
    allowEmpty: true,
    maxLength: ELEMENT_KIND_MAX_LENGTH,
  });
  const characterIdentityKey = text(
    target.characterIdentityKey,
    "focus world point request.target.characterIdentityKey",
    { allowEmpty: true, maxLength: IDENTIFIER_MAX_LENGTH },
  );
  const pointKind = text(target.pointKind, "focus world point request.target.pointKind", {
    allowEmpty: true,
    maxLength: 16,
  });
  if (!POINT_KINDS.has(pointKind)) {
    throw new TypeError("focus world point request.target.pointKind is unsupported");
  }
  if (
    characterIdentityKey &&
    (elementKind !== "character" || pointKind !== "character")
  ) {
    throw new TypeError(
      "focus world point request Character identity requires a Character target",
    );
  }
  const historyBehavior = text(
    target.historyBehavior,
    "focus world point request.target.historyBehavior",
    { maxLength: 8 },
  );
  if (!HISTORY_BEHAVIORS.has(historyBehavior)) {
    throw new TypeError("focus world point request.target.historyBehavior is unsupported");
  }
  return frozenJson({
    version: CONTRACT_VERSION,
    target: {
      elementKind,
      characterIdentityKey,
      worldX: finiteNumber(target.worldX, "focus world point request.target.worldX"),
      worldZ: finiteNumber(target.worldZ, "focus world point request.target.worldZ"),
      pointKind,
      pointLabel: text(target.pointLabel, "focus world point request.target.pointLabel", {
        allowEmpty: true,
        maxLength: LABEL_MAX_LENGTH,
      }),
      historyBehavior,
    },
  });
}

export function normalizeBookmarksChangeDetail(value) {
  const source = versionedRecord(
    value,
    ["entries", "presentation"],
    "bookmarks change",
  );
  if (!Array.isArray(source.entries) || source.entries.length > BOOKMARK_ENTRY_LIMIT) {
    throw new TypeError(
      `bookmarks change.entries must be an array of at most ${BOOKMARK_ENTRY_LIMIT} entries`,
    );
  }
  const jsonBudget = { nodes: 0 };
  const entries = source.entries.map((entry, index) =>
    normalizeStoredBookmark(
      entry,
      `bookmarks change.entries[${index}]`,
      jsonBudget,
    ));
  const entryIds = new Set();
  for (const entry of entries) {
    if (entryIds.has(entry.id)) {
      throw new TypeError(`bookmarks change.entries contains duplicate id ${entry.id}`);
    }
    entryIds.add(entry.id);
  }
  const presentation = record(source.presentation, "bookmarks change.presentation");
  assertExactKeys(
    presentation,
    ["placing", "selectedIds"],
    "bookmarks change.presentation",
  );
  if (typeof presentation.placing !== "boolean") {
    throw new TypeError("bookmarks change.presentation.placing must be a boolean");
  }
  if (
    !Array.isArray(presentation.selectedIds) ||
    presentation.selectedIds.length > BOOKMARK_ENTRY_LIMIT
  ) {
    throw new TypeError(
      `bookmarks change.presentation.selectedIds must be an array of at most ${BOOKMARK_ENTRY_LIMIT} entries`,
    );
  }
  const selectedIds = [];
  const seenSelectedIds = new Set();
  for (let index = 0; index < presentation.selectedIds.length; index += 1) {
    const id = text(
      presentation.selectedIds[index],
      `bookmarks change.presentation.selectedIds[${index}]`,
      { maxLength: IDENTIFIER_MAX_LENGTH },
    );
    if (seenSelectedIds.has(id)) {
      throw new TypeError(`bookmarks change.presentation.selectedIds contains duplicate id ${id}`);
    }
    if (!entryIds.has(id)) {
      throw new TypeError(`bookmarks change.presentation.selectedIds contains unknown id ${id}`);
    }
    seenSelectedIds.add(id);
    selectedIds.push(id);
  }
  const normalized = {
    version: CONTRACT_VERSION,
    entries,
    presentation: {
      placing: presentation.placing,
      selectedIds,
    },
  };
  assertBookmarkDetailSize(normalized);
  return frozenJson(normalized);
}

export function normalizeZoneLootProfileChangeDetail(value) {
  const source = versionedRecord(value, ["profile"], "zone loot profile change");
  const profile = text(source.profile, "zone loot profile change.profile", { maxLength: 16 });
  if (!LOOT_PROFILES.has(profile)) {
    throw new TypeError("zone loot profile change.profile is unsupported");
  }
  return frozenJson({ version: CONTRACT_VERSION, profile });
}

export function normalizeLootConditionOptionSelectDetail(value) {
  const source = versionedRecord(
    value,
    ["selectionKey", "optionIndex"],
    "loot condition option select",
  );
  return frozenJson({
    version: CONTRACT_VERSION,
    selectionKey: text(source.selectionKey, "loot condition option select.selectionKey", {
      maxLength: SELECTION_KEY_MAX_LENGTH,
    }),
    optionIndex: nonNegativeSafeInteger(
      source.optionIndex,
      "loot condition option select.optionIndex",
    ),
  });
}

export function normalizeZoneInfoTabChangeDetail(value) {
  const source = versionedRecord(value, ["paneId"], "zone info tab change");
  const paneId = text(source.paneId, "zone info tab change.paneId", { maxLength: 16 });
  if (!ZONE_INFO_PANES.has(paneId)) {
    throw new TypeError("zone info tab change.paneId is unsupported");
  }
  return frozenJson({ version: CONTRACT_VERSION, paneId });
}

export function normalizeCharacterActivityTargetChangeDetail(value) {
  const source = versionedRecord(
    value,
    ["identityKey"],
    "Character activity target change",
  );
  return frozenJson({
    version: CONTRACT_VERSION,
    identityKey: text(source.identityKey, "Character activity target change.identityKey", {
      allowEmpty: true,
      maxLength: IDENTIFIER_MAX_LENGTH,
    }),
  });
}

const NORMALIZER_BY_EVENT_TYPE = Object.freeze({
  [FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT]: normalizeFocusWorldPointRequestDetail,
  [FISHYMAP_BOOKMARKS_CHANGE_EVENT]: normalizeBookmarksChangeDetail,
  [FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT]: normalizeZoneLootProfileChangeDetail,
  [FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT]: normalizeLootConditionOptionSelectDetail,
  [FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT]: normalizeZoneInfoTabChangeDetail,
  [FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT]:
    normalizeCharacterActivityTargetChangeDetail,
});

export function dispatchMapPanelEvent(
  target,
  eventType,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  const normalize = NORMALIZER_BY_EVENT_TYPE[eventType];
  if (!normalize) {
    throw new TypeError(`Unsupported FishyMap panel event type ${String(eventType)}`);
  }
  const normalizedDetail = normalize(detail);
  if (
    !target ||
    typeof target.dispatchEvent !== "function" ||
    typeof customEventConstructor !== "function"
  ) {
    return false;
  }
  return target.dispatchEvent(
    new customEventConstructor(eventType, {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: normalizedDetail,
    }),
  ) !== false;
}

export function dispatchFocusWorldPointRequest(target, targetDetail) {
  return dispatchMapPanelEvent(target, FISHYMAP_FOCUS_WORLD_POINT_REQUEST_EVENT, {
    version: CONTRACT_VERSION,
    target: targetDetail,
  });
}

export function dispatchBookmarksChange(target, entries, presentation) {
  return dispatchMapPanelEvent(target, FISHYMAP_BOOKMARKS_CHANGE_EVENT, {
    version: CONTRACT_VERSION,
    entries,
    presentation,
  });
}

export function dispatchZoneLootProfileChange(target, profile) {
  return dispatchMapPanelEvent(target, FISHYMAP_ZONE_LOOT_PROFILE_CHANGE_EVENT, {
    version: CONTRACT_VERSION,
    profile,
  });
}

export function dispatchLootConditionOptionSelect(target, selectionKey, optionIndex) {
  return dispatchMapPanelEvent(target, FISHYMAP_LOOT_CONDITION_OPTION_SELECT_EVENT, {
    version: CONTRACT_VERSION,
    selectionKey,
    optionIndex,
  });
}

export function dispatchZoneInfoTabChange(target, paneId) {
  return dispatchMapPanelEvent(target, FISHYMAP_ZONE_INFO_TAB_CHANGE_EVENT, {
    version: CONTRACT_VERSION,
    paneId,
  });
}

export function dispatchCharacterActivityTargetChange(target, identityKey) {
  return dispatchMapPanelEvent(
    target,
    FISHYMAP_CHARACTER_ACTIVITY_TARGET_CHANGE_EVENT,
    { version: CONTRACT_VERSION, identityKey },
  );
}

function requiredProjection(projection) {
  assertMapShellProjectionV1(projection);
  return projection;
}

export function focusWorldPointPatchFromPanelEvent(detail, projection) {
  const normalized = normalizeFocusWorldPointRequestDetail(detail);
  const current = requiredProjection(projection);
  const patch = buildFocusWorldPointSignalPatch(normalized.target, {
    _map_ui: current.ui,
    _map_actions: current.actions,
    _map_session: current.session,
  });
  if (!patch) {
    throw new TypeError("focus world point request did not produce a signal patch");
  }
  return frozenJson(patch);
}

export function bookmarksPatchFromPanelEvent(detail, projection) {
  const normalized = normalizeBookmarksChangeDetail(detail);
  requiredProjection(projection);
  return frozenJson({
    _map_bookmarks: { entries: normalized.entries },
    _map_ui: { bookmarks: normalized.presentation },
  });
}

export function zoneLootProfilePatchFromPanelEvent(detail, projection) {
  const normalized = normalizeZoneLootProfileChangeDetail(detail);
  requiredProjection(projection);
  return frozenJson({
    _map_ui: { windowUi: { zoneInfo: { lootProfile: normalized.profile } } },
  });
}

export function lootConditionOptionPatchFromPanelEvent(detail, projection) {
  const normalized = normalizeLootConditionOptionSelectDetail(detail);
  const current = requiredProjection(projection);
  const currentSelection = isPlainObject(current.bridged?.filters?.lootConditionSelection)
    ? cloneJson(current.bridged.filters.lootConditionSelection)
    : {};
  return frozenJson({
    _map_bridged: {
      filters: {
        lootConditionSelection: {
          ...currentSelection,
          [normalized.selectionKey]: normalized.optionIndex,
        },
      },
    },
  });
}

export function zoneInfoTabPatchFromPanelEvent(detail, projection) {
  const normalized = normalizeZoneInfoTabChangeDetail(detail);
  requiredProjection(projection);
  return frozenJson({
    _map_ui: { windowUi: { zoneInfo: { tab: normalized.paneId } } },
  });
}

export function characterActivityTransitionFromPanelEvent(detail, projection) {
  const normalized = normalizeCharacterActivityTargetChangeDetail(detail);
  const current = requiredProjection(projection);
  if ((current.characterActivity?.identityKey ?? "") === normalized.identityKey) {
    return frozenJson({ patch: {}, refresh: false });
  }
  return frozenJson({
    patch: {
      _map_character_activity: { identityKey: normalized.identityKey },
    },
    refresh: true,
  });
}
